<template>
  <div class="modal fade" :class="{ show: show }" :style="{ display: show ? 'block' : 'none' }" tabindex="-1" @click.self="closeModal">
    <div class="modal-dialog modal-xl modal-dialog-centered modal-dialog-scrollable">
      <div class="modal-content">
        <div class="modal-header">
          <h5 class="modal-title">Complete Revenue Collectors Ranking - {{ getStreamName() }}</h5>
          <div class="ms-auto me-3">
            <span class="badge bg-info">{{ getDateRangeLabel() }}</span>
          </div>
          <button type="button" class="btn-close" @click="closeModal"></button>
        </div>

        <div class="modal-body">
          <!-- Loading State -->
          <div v-if="loading" class="text-center py-5">
            <div class="spinner-border text-primary" role="status">
              <span class="visually-hidden">Loading...</span>
            </div>
            <p class="mt-3">Loading collectors data...</p>
          </div>

          <!-- Content when not loading -->
          <div v-else>
            <!-- Summary Cards -->
            <div class="row mb-4">
              <div class="col-md-3">
                <div class="card bg-primary text-white">
                  <div class="card-body">
                    <h6 class="text-white-50">Total Collectors</h6>
                    <h3 class="text-white mb-0">{{ totalUsers }}</h3>
                  </div>
                </div>
              </div>
              <div class="col-md-3">
                <div class="card bg-success text-white">
                  <div class="card-body">
                    <h6 class="text-white-50">Total Revenue</h6>
                    <h3 class="text-white mb-0">KES {{ numberFormat(totalRevenue) }}</h3>
                  </div>
                </div>
              </div>
              <div class="col-md-3">
                <div class="card bg-warning text-white">
                  <div class="card-body">
                    <h6 class="text-white-50">Average Revenue</h6>
                    <h3 class="text-white mb-0">KES {{ numberFormat(averageRevenue) }}</h3>
                  </div>
                </div>
              </div>
              <div class="col-md-3">
                <div class="card bg-info text-white">
                  <div class="card-body">
                    <h6 class="text-white-50">Achievement Rate</h6>
                    <h3 class="text-white mb-0">{{ averageAchievementRate }}%</h3>
                  </div>
                </div>
              </div>
            </div>

            <!-- Filters -->
            <div class="row mb-3">
              <div class="col-md-4">
                <div class="input-group">
                  <span class="input-group-text"><i class="bx bx-search"></i></span>
                  <input
                      type="text"
                      class="form-control"
                      placeholder="Search by name or ID..."
                      v-model="searchQuery"
                      @input="filterUsers"
                  >
                </div>
              </div>
              <div class="col-md-3">
                <select class="form-select" v-model="filterZone" @change="filterUsers">
                  <option value="">All Zones</option>
                  <option v-for="zone in uniqueZones" :key="zone" :value="zone">{{ zone || 'Unassigned' }}</option>
                </select>
              </div>
              <div class="col-md-3">
                <select class="form-select" v-model="filterPerformance" @change="filterUsers">
                  <option value="all">All Performers</option>
                  <option value="top">Top Performers (>100%)</option>
                  <option value="average">Average (50-100%)</option>
                  <option value="below">Below Average (&lt;50%)</option>
                </select>
              </div>
              <div class="col-md-2">
                <button class="btn btn-sm btn-secondary w-100" @click="resetFilters">
                  <i class="bx bx-reset"></i> Reset
                </button>
              </div>
            </div>

            <!-- Results count -->
            <div class="mb-2">
              <small class="text-muted">Showing {{ filteredUsers.length }} of {{ allUsers.length }} collectors</small>
            </div>

            <!-- Users Table -->
            <div class="table-responsive" style="max-height: 500px;">
              <table class="table table-hover">
                <thead class="sticky-top bg-light">
                <tr>
                  <th @click="sortBy('rank')" style="cursor: pointer;">
                    Rank <i class="bx" :class="getSortIcon('rank')"></i>
                  </th>
                  <th @click="sortBy('idNo')" style="cursor: pointer;">
                    ID <i class="bx" :class="getSortIcon('idNo')"></i>
                  </th>
                  <th @click="sortBy('names')" style="cursor: pointer;">
                    Name <i class="bx" :class="getSortIcon('names')"></i>
                  </th>
                  <th @click="sortBy('zone')" style="cursor: pointer;">
                    Zone <i class="bx" :class="getSortIcon('zone')"></i>
                  </th>
                  <th class="text-end" @click="sortBy('totalRevenue')" style="cursor: pointer;">
                    Revenue <i class="bx" :class="getSortIcon('totalRevenue')"></i>
                  </th>
                  <th class="text-end" @click="sortBy('transactionCount')" style="cursor: pointer;">
                    Transactions <i class="bx" :class="getSortIcon('transactionCount')"></i>
                  </th>
                  <th class="text-end" @click="sortBy('achievementRate')" style="cursor: pointer;">
                    Achievement <i class="bx" :class="getSortIcon('achievementRate')"></i>
                  </th>
                  <th class="text-center">Actions</th>
                </tr>
                </thead>
                <tbody>
                <tr v-for="user in paginatedUsers"
                    :key="user.idNo"
                    :class="{ 'table-success': user.achievementRate > 100, 'table-warning': user.achievementRate < 50 }">
                  <td>
                      <span class="badge" :class="getRankBadgeClass(user.rank)">
                        #{{ user.rank }}
                      </span>
                  </td>
                  <td>{{ user.idNo }}</td>
                  <td>
                    <div>
                      <div class="fw-bold">{{ user.names }}</div>
                      <small class="text-muted">{{ user.trend }} trend</small>
                    </div>
                  </td>
                  <td>{{ user.zone || 'Unassigned' }}</td>
                  <td class="text-end">
                    <div>
                      <div class="fw-bold">KES {{ numberFormat(user.totalRevenue) }}</div>
                      <small class="text-muted">Avg: {{ numberFormat(user.averageTransaction) }}</small>
                    </div>
                  </td>
                  <td class="text-end">{{ numberFormat(user.transactionCount) }}</td>
                  <td class="text-end">
                    <div class="progress" style="width: 100px; height: 20px;">
                      <div class="progress-bar"
                           :class="getProgressBarClass(user.achievementRate)"
                           :style="{ width: Math.min(user.achievementRate, 100) + '%' }">
                        {{ user.achievementRate }}%
                      </div>
                    </div>
                  </td>
                  <td class="text-center">
                    <button class="btn btn-sm btn-primary" @click="viewUserDetails(user.idNo)">
                      <i class="bx bx-show"></i> View
                    </button>
                  </td>
                </tr>
                </tbody>
              </table>
            </div>

            <!-- Pagination -->
            <div class="row mt-3" v-if="totalPages > 1">
              <div class="col-12">
                <nav>
                  <ul class="pagination justify-content-center">
                    <li class="page-item" :class="{ disabled: currentPage === 1 }">
                      <a class="page-link" @click="currentPage = 1" href="javascript:void(0);">First</a>
                    </li>
                    <li class="page-item" :class="{ disabled: currentPage === 1 }">
                      <a class="page-link" @click="currentPage = currentPage - 1" href="javascript:void(0);">Previous</a>
                    </li>

                    <li class="page-item" v-for="page in visiblePages" :key="page"
                        :class="{ active: page === currentPage }">
                      <a class="page-link" @click="currentPage = page" href="javascript:void(0);">{{ page }}</a>
                    </li>

                    <li class="page-item" :class="{ disabled: currentPage === totalPages }">
                      <a class="page-link" @click="currentPage = currentPage + 1" href="javascript:void(0);">Next</a>
                    </li>
                    <li class="page-item" :class="{ disabled: currentPage === totalPages }">
                      <a class="page-link" @click="currentPage = totalPages" href="javascript:void(0);">Last</a>
                    </li>
                  </ul>
                </nav>
              </div>
            </div>
          </div>
        </div>

        <div class="modal-footer">
          <button type="button" class="btn btn-secondary" @click="closeModal">Close</button>
          <button type="button" class="btn btn-primary" @click="exportAllUsers" :disabled="loading || allUsers.length === 0">
            <i class="bx bx-download"></i> Export All
          </button>
        </div>
      </div>
    </div>
  </div>

  <!-- Modal Backdrop -->
  <div class="modal-backdrop fade" :class="{ show: show }" v-if="show"></div>

  <!-- User Details Modal -->
  <UserDetailsModal
      v-if="showUserDetails"
      :show="showUserDetails"
      :userId="selectedUserId"
      :stream-prefix="streamPrefix"
      :date-range="dateRange"
      @close="closeUserDetails"
  />
</template>

<script>
/* eslint-disable */
import { execute, biller } from "@/api";
import UserDetailsModal from "./UserDetailsModal";

export default {
  name: "AllUsersRankingModal",
  components: { UserDetailsModal },
  props: {
    show: {
      type: Boolean,
      default: false
    },
    streamPrefix: {
      type: String,
      default: 'MKT'
    },
    dateRange: {
      type: Object,
      default: () => ({ type: 'this_month', startDate: '', endDate: '' })
    }
  },
  data() {
    return {
      allUsers: [],
      filteredUsers: [],
      loading: false,
      searchQuery: '',
      filterZone: '',
      filterPerformance: 'all',
      sortColumn: 'rank',
      sortDirection: 'asc',
      currentPage: 1,
      itemsPerPage: 20,
      showUserDetails: false,
      selectedUserId: null
    }
  },
  computed: {
    totalUsers() {
      return this.allUsers.length;
    },
    totalRevenue() {
      return this.allUsers.reduce((sum, user) => sum + user.totalRevenue, 0);
    },
    averageRevenue() {
      return this.totalUsers > 0 ? Math.round(this.totalRevenue / this.totalUsers) : 0;
    },
    averageAchievementRate() {
      if (this.totalUsers === 0) return 0;
      const totalRate = this.allUsers.reduce((sum, user) => sum + user.achievementRate, 0);
      return Math.round(totalRate / this.totalUsers);
    },
    uniqueZones() {
      const zones = [...new Set(this.allUsers.map(user => user.zone))];
      return zones.sort();
    },
    paginatedUsers() {
      const start = (this.currentPage - 1) * this.itemsPerPage;
      const end = start + this.itemsPerPage;
      return this.filteredUsers.slice(start, end);
    },
    totalPages() {
      return Math.ceil(this.filteredUsers.length / this.itemsPerPage);
    },
    visiblePages() {
      const pages = [];
      const maxVisible = 5;
      let start = Math.max(1, this.currentPage - Math.floor(maxVisible / 2));
      let end = Math.min(this.totalPages, start + maxVisible - 1);

      if (end - start < maxVisible - 1) {
        start = Math.max(1, end - maxVisible + 1);
      }

      for (let i = start; i <= end; i++) {
        pages.push(i);
      }
      return pages;
    }
  },
  watch: {
    show(newVal) {
      if (newVal && this.streamPrefix) {
        this.fetchAllUsers();
      }
    },
    streamPrefix(newVal) {
      if (this.show && newVal) {
        this.fetchAllUsers();
      }
    },
    dateRange: {
      handler(newVal, oldVal) {
        if (this.show && this.streamPrefix &&
            (newVal.type !== oldVal?.type || newVal.startDate !== oldVal?.startDate || newVal.endDate !== oldVal?.endDate)) {
          this.fetchAllUsers();
        }
      },
      deep: true
    }
  },
  methods: {
    getStreamName() {
      const streamNames = {
        'MKT': 'Markets',
        'PKN': 'Parking',
        'SBP': 'Single Business Permit',
        'CESS': 'Cess',
        'CLMP': 'Clamping',
        'RNT': 'Housing Rent',
        'ADV': 'Advertisements'
      };
      return streamNames[this.streamPrefix] || this.streamPrefix;
    },

    getDateRangeLabel() {
      switch (this.dateRange.type) {
        case 'today':
          return 'Today';
        case 'yesterday':
          return 'Yesterday';
        case 'this_week':
          return 'This Week';
        case 'this_month':
          return 'This Month';
        case 'custom':
          return `${this.dateRange.startDate} to ${this.dateRange.endDate}`;
        default:
          return 'Current Period';
      }
    },

    async fetchAllUsers() {
      if (!this.streamPrefix) {
        console.log('No stream prefix provided');
        return;
      }

      this.loading = true;
      try {
        const data = new FormData();
        data.append("function", "getMarketsRevenuePerformance");
        data.append("incomeTypePrefix", this.streamPrefix);
        data.append("limit", "1000"); // Get more users

        // Add date range parameters
        data.append("dateRange", this.dateRange.type);
        if (this.dateRange.type === 'custom') {
          data.append("startDate", this.dateRange.startDate);
          data.append("endDate", this.dateRange.endDate);
        }

        console.log('Fetching all users with params:', {
          stream: this.streamPrefix,
          dateRange: this.dateRange.type,
          startDate: this.dateRange.startDate,
          endDate: this.dateRange.endDate
        });

        const response = await execute(data, biller);

        if (response.data.success) {
          const result = response.data.data.marketsRevenuePerformance;

          // Get all users from the actual API response
          const allUsersData = await this.fetchAllUsersData();

          this.allUsers = allUsersData;
          this.filteredUsers = [...this.allUsers];
        }
      } catch (error) {
        console.error("Error fetching users:", error);
        this.$toast?.error(`Failed to load users for ${this.getStreamName()}`);
      } finally {
        this.loading = false;
      }
    },

    async fetchAllUsersData() {
      // Fetch comprehensive user data for the specific stream and date range
      const data = new FormData();
      data.append("function", "getMarketsRevenuePerformance");
      data.append("incomeTypePrefix", this.streamPrefix);
      data.append("limit", "1000"); // Get more users

      // Add date range parameters
      data.append("dateRange", this.dateRange.type);
      if (this.dateRange.type === 'custom') {
        data.append("startDate", this.dateRange.startDate);
        data.append("endDate", this.dateRange.endDate);
      }

      try {
        const response = await execute(data, biller);
        if (response.data.success && response.data.data.marketsRevenuePerformance) {
          const performanceData = response.data.data.marketsRevenuePerformance;

          // Get all users from different sources in the response
          const allUsers = [];

          // Add top performers
          if (performanceData.userPerformance?.topPerformers) {
            allUsers.push(...performanceData.userPerformance.topPerformers);
          }

          // Add bottom performers
          if (performanceData.userPerformance?.bottomPerformers) {
            allUsers.push(...performanceData.userPerformance.bottomPerformers);
          }

          // Remove duplicates based on idNo
          const uniqueUsers = Array.from(
              new Map(allUsers.map(user => [user.idNo, user])).values()
          );

          // Sort by revenue and add ranks
          return uniqueUsers
              .sort((a, b) => b.totalRevenue - a.totalRevenue)
              .map((user, index) => ({
                ...user,
                rank: index + 1
              }));
        }
      } catch (error) {
        console.error("Error in fetchAllUsersData:", error);
      }

      // Fallback: return empty array instead of fake data
      return [];
    },

    filterUsers() {
      let filtered = [...this.allUsers];

      // Search filter
      if (this.searchQuery) {
        const query = this.searchQuery.toLowerCase();
        filtered = filtered.filter(user =>
            user.names.toLowerCase().includes(query) ||
            user.idNo.includes(query)
        );
      }

      // Zone filter
      if (this.filterZone) {
        filtered = filtered.filter(user => user.zone === this.filterZone);
      }

      // Performance filter
      if (this.filterPerformance !== 'all') {
        if (this.filterPerformance === 'top') {
          filtered = filtered.filter(user => user.achievementRate > 100);
        } else if (this.filterPerformance === 'average') {
          filtered = filtered.filter(user => user.achievementRate >= 50 && user.achievementRate <= 100);
        } else if (this.filterPerformance === 'below') {
          filtered = filtered.filter(user => user.achievementRate < 50);
        }
      }

      this.filteredUsers = filtered;
      this.currentPage = 1; // Reset to first page
    },

    sortBy(column) {
      if (this.sortColumn === column) {
        this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc';
      } else {
        this.sortColumn = column;
        this.sortDirection = 'asc';
      }

      this.filteredUsers.sort((a, b) => {
        let aVal = a[column];
        let bVal = b[column];

        if (typeof aVal === 'string') {
          aVal = aVal.toLowerCase();
          bVal = bVal.toLowerCase();
        }

        if (this.sortDirection === 'asc') {
          return aVal > bVal ? 1 : -1;
        } else {
          return aVal < bVal ? 1 : -1;
        }
      });
    },

    getSortIcon(column) {
      if (this.sortColumn !== column) {
        return 'bx-sort';
      }
      return this.sortDirection === 'asc' ? 'bx-sort-up' : 'bx-sort-down';
    },

    resetFilters() {
      this.searchQuery = '';
      this.filterZone = '';
      this.filterPerformance = 'all';
      this.filteredUsers = [...this.allUsers];
      this.currentPage = 1;
    },

    getRankBadgeClass(rank) {
      if (rank <= 3) return 'bg-success';
      if (rank <= 10) return 'bg-primary';
      if (rank <= 20) return 'bg-info';
      return 'bg-secondary';
    },

    getProgressBarClass(rate) {
      if (rate > 100) return 'bg-success';
      if (rate >= 75) return 'bg-info';
      if (rate >= 50) return 'bg-warning';
      return 'bg-danger';
    },

    viewUserDetails(userId) {
      console.log('AllUsersRankingModal - viewUserDetails called with:', userId, 'stream:', this.streamPrefix, 'dateRange:', this.dateRange);
      this.selectedUserId = userId;
      this.showUserDetails = true;
      console.log('AllUsersRankingModal - state after:', {
        selectedUserId: this.selectedUserId,
        showUserDetails: this.showUserDetails,
        streamPrefix: this.streamPrefix,
        dateRange: this.dateRange
      });
    },

    closeUserDetails() {
      this.showUserDetails = false;
      this.selectedUserId = null;
    },

    closeModal() {
      this.$emit('close');
    },

    numberFormat(num) {
      return new Intl.NumberFormat('en-GB').format(num || 0);
    },

    exportAllUsers() {
      let csv = `${this.getStreamName()} Revenue Collectors Report\n`;
      csv += `Generated: ${new Date().toLocaleString()}\n`;
      csv += `Stream: ${this.streamPrefix}\n`;
      csv += `Period: ${this.getDateRangeLabel()}\n\n`;

      // Summary
      csv += 'SUMMARY\n';
      csv += `Total Collectors,${this.totalUsers}\n`;
      csv += `Total Revenue,${this.totalRevenue}\n`;
      csv += `Average Revenue,${this.averageRevenue}\n`;
      csv += `Average Achievement Rate,${this.averageAchievementRate}%\n\n`;

      // User Data
      csv += 'COLLECTOR DETAILS\n';
      csv += 'Rank,ID Number,Name,Zone,Revenue,Transactions,Average Transaction,Achievement Rate,Trend\n';

      this.allUsers.forEach(user => {
        csv += `${user.rank},"${user.idNo}","${user.names}","${user.zone || 'Unassigned'}",${user.totalRevenue},${user.transactionCount},${user.averageTransaction},${user.achievementRate}%,${user.trend}\n`;
      });

      // Download
      const blob = new Blob([csv], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${this.streamPrefix}_collectors_ranking_${this.dateRange.type}_${new Date().toISOString().split('T')[0]}.csv`;
      a.click();
    }
  }
}
</script>

<style scoped>
.modal.show {
  display: block !important;
}

.modal-backdrop {
  position: fixed;
  top: 0;
  left: 0;
  z-index: 1040;
  width: 100vw;
  height: 100vh;
  background-color: #000;
  opacity: 0.5;
}

.table-responsive {
  overflow-y: auto;
}

.table thead th {
  position: sticky;
  top: 0;
  z-index: 10;
  background-color: #f8f9fa;
}

.progress {
  background-color: #e9ecef;
}

.badge {
  padding: 0.35rem 0.65rem;
}

/* Custom scrollbar */
.table-responsive::-webkit-scrollbar {
  width: 8px;
  height: 8px;
}

.table-responsive::-webkit-scrollbar-track {
  background: #f1f1f1;
}

.table-responsive::-webkit-scrollbar-thumb {
  background: #888;
  border-radius: 4px;
}

.table-responsive::-webkit-scrollbar-thumb:hover {
  background: #555;
}

/* Hover effects */
.table tbody tr:hover {
  background-color: #f5f5f5;
}

/* Sort indicators */
th[style*="cursor: pointer"]:hover {
  background-color: #e9ecef;
}
</style>